import React, { useEffect, useState } from 'react';
import { Button, Card, Progress, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import ApiService from '../../core/service/api-service';
import { useChangelog } from '../../core/context/ChangelogContext';
import { useHistory } from 'react-router-dom';
import { Pages } from '../../AppRouter';
import { useUser } from '../../core/context/UserContext';

export default function ChangelogWizard() {
    const { releases, loading: contextLoading } = useChangelog();
    const { user } = useUser();
    const { t, i18n } = useTranslation();
    const history = useHistory();

    const [flattenedSteps, setFlattenedSteps] = useState([]);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [selectedLanguage, setSelectedLanguage] = useState((i18n.language || 'tr').substring(0, 2));
    const { Option } = Select;

    useEffect(() => {
        if (releases && releases.length > 0) {
            const steps = [];
            releases.forEach(r => {
                if (r.steps && r.steps.length > 0) {
                    r.steps.forEach(s => {
                        steps.push({ version: r.version, step: s });
                    });
                }
            });
            // Add Final Step
            steps.push({ version: 'Done', step: 'Ready', isFinal: true });

            setFlattenedSteps(steps);
        } else if (!contextLoading && (!releases || releases.length === 0)) {
            history.push(Pages.Dashboard.Home);
        }
    }, [releases, contextLoading]);

    // Google Translate Removed

    // Load content effect removed

    // Keyboard Navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (flattenedSteps.length === 0) return;

            if (e.key === 'ArrowRight') {
                if (currentStepIndex < flattenedSteps.length - 1) {
                    const nextIndex = currentStepIndex + 1;
                    setCurrentStepIndex(nextIndex);
                    // loadContent(flattenedSteps[nextIndex]); // Removed: handled by effect
                }
            } else if (e.key === 'ArrowLeft') {
                if (currentStepIndex > 0) {
                    const prevIndex = currentStepIndex - 1;
                    setCurrentStepIndex(prevIndex);
                    // loadContent(flattenedSteps[prevIndex]); // Removed: handled by effect
                }
            } else if (e.key === 'Enter') {
                const currentStep = flattenedSteps[currentStepIndex];
                if (currentStep && currentStep.isFinal) {
                    handleFinish();
                }
            }
        };

        const handleMessage = (e) => {
            if (e.data && e.data.type === 'changelog-keydown') {
                handleKeyDown({ key: e.data.key });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('message', handleMessage);
        };
    }, [currentStepIndex, flattenedSteps]);

    // loadContent function removed

    const handleNext = async () => {
        if (currentStepIndex < flattenedSteps.length - 1) {
            const nextIndex = currentStepIndex + 1;
            setCurrentStepIndex(nextIndex);
            // loadContent(flattenedSteps[nextIndex]); // Removed: handled by effect
        }
    };

    const handlePrev = () => {
        if (currentStepIndex > 0) {
            const prevIndex = currentStepIndex - 1;
            setCurrentStepIndex(prevIndex);
            // loadContent(flattenedSteps[prevIndex]); // Removed: handled by effect
        }
    }

    const handleFinish = async () => {
        try {
            // The last real step is at length - 2 (since last is 'Done')
            // Or simply find the last version from the flattened list excluding 'Done'
            const realSteps = flattenedSteps.filter(s => !s.isFinal);
            const lastRealStep = realSteps[realSteps.length - 1];

            if (lastRealStep) {
                await ApiService.User.UpdateLastSeenVersion(lastRealStep.version);
            }
            history.push('/');
        } catch (err) {
            console.error("Error updating version", err);
        }
    }

    if (contextLoading) return <div style={{ padding: 50, textAlign: 'center' }}>Loading...</div>;
    if (flattenedSteps.length === 0) return null;

    const currentStep = flattenedSteps[currentStepIndex];
    const progressPercent = Math.round(((currentStepIndex) / (flattenedSteps.length - 1)) * 100);

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Card
                title={
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span>{t('What\'s New')}</span>
                                <Select
                                    value={selectedLanguage}
                                    onChange={(val) => {
                                        setSelectedLanguage(val);
                                        // Update body class inside iframe if needed
                                        // But iframe reload due to src change handles it via URL param script
                                    }}
                                    size="small"
                                    style={{ width: 100 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Option value="tr">Türkçe</Option>
                                    <Option value="en">English</Option>
                                    <Option value="ru">Русский</Option>
                                    <Option value="uz">O'zbek</Option>
                                </Select>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
                                {currentStep.isFinal ? t('Completed') : currentStep.version}
                            </span>
                        </div>
                        <Progress percent={progressPercent} showInfo={false} size="small" />
                    </div>
                }
                style={{ width: '100%', maxWidth: 900, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >


                {currentStep.isFinal ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <h2>{t('You are all up to date!')}</h2>
                        <p>{t('Click the button below to start using the new version.')}</p>
                        <Button type="primary" size="large" onClick={handleFinish} style={{ marginTop: 20 }}>
                            {t('Start Using')}
                        </Button>
                    </div>
                ) : (
                    <>
                        <iframe
                            src={`/changelogs/${currentStep.version}/${currentStep.step}.html?lang=${selectedLanguage}&permList=${[...(user?.data?.permissionIdList || []), ...(user?.data?.role?.permissionIdList || [])].join(',')}`}
                            style={{ width: '100%', height: '60vh', border: 'none', marginBottom: 20, minHeight: 200 }}
                            title="Changelog Content"
                        />

                        <div style={{ textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
                            {currentStepIndex > 0 && (
                                <Button style={{ marginRight: 8 }} onClick={handlePrev}>
                                    {t('Previous')}
                                </Button>
                            )}
                            <Button type="primary" onClick={handleNext} size="large">
                                {t('Next')}
                            </Button>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}
